/**
 * Loads GA4 (gtag) only after analytics consent.
 * (Ahrefs Web Analytics is injected into HTML in server/vike-handler.ts and does not depend on jc_consent_v1.)
 * Single entry point used from +client.ts and after the user accepts cookies.
 */

import { readConsentFromBrowser } from './document-cookie';
import { hasAnalyticsConsent } from './has-analytics-consent';
import { pushGoogleConsentGrantedForAnalytics } from './push-google-consent-granted';
import { isInternalQaExcludedInBrowser } from '../analytics/internal-qa-exclusion';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    gtag_initialized?: boolean;
    gtag_ready?: boolean;
  }
}

const GA_SCRIPT_ID = 'ga4-loader';

/** Avoid duplicate idle listeners if bootstrap is invoked more than once. */
let analyticsBootstrapScheduled = false;

/** True after we queued gtag('js') + gtag('config') onto dataLayer (script may still be loading). */
let gaBootstrapCommandsQueued = false;

/**
 * Install GA4 dataLayer + gtag shim immediately so early page_view / events queue until gtag.js loads.
 * Defer only the network fetch of gtag/js to idle / first interaction (TBT-friendly).
 */
function loadGoogleAnalyticsOnce(): void {
  const GA_ID = import.meta.env.PUBLIC_ENV__GOOGLE_ANALYTICS;
  if (!GA_ID) return;

  try {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer!.push(args);
      };
    }
    if (!gaBootstrapCommandsQueued) {
      gaBootstrapCommandsQueued = true;
      // Consent Mode v2: grant analytics storage before loading gtag/js (defaults are set in +Head).
      pushGoogleConsentGrantedForAnalytics(window.gtag);
      window.gtag('js', new Date());
      window.gtag('config', GA_ID);
      window.gtag_initialized = true;
      window.gtag_ready = true;
    }
  } catch {
    /* swallow */
  }

  const appendGtagScriptOnce = () => {
    try {
      if (document.getElementById(GA_SCRIPT_ID)) return;
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
      s.id = GA_SCRIPT_ID;
      (document.head || document.documentElement).appendChild(s);
    } catch {
      /* swallow */
    }
  };

  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(appendGtagScriptOnce, { timeout: 3000 });
  } else {
    setTimeout(appendGtagScriptOnce, 1);
  }
  document.addEventListener('pointerdown', appendGtagScriptOnce, { once: true });
}

/**
 * Idempotent: schedules GA if consent cookie allows analytics.
 */
export function runAnalyticsConsentBootstrap(): void {
  if (typeof window === 'undefined') return;
  if (isInternalQaExcludedInBrowser()) return;
  const consent = readConsentFromBrowser();
  if (!hasAnalyticsConsent(consent)) return;
  if (analyticsBootstrapScheduled) return;
  analyticsBootstrapScheduled = true;

  loadGoogleAnalyticsOnce();
}
